import { UseFormGetValues } from 'react-hook-form'
import { ICreateNotification } from 'types/notification'
import { damage_level, isSevereOrDeathDamage as isSevereOrDeathDamage, isModerateOrMildDamage as isMildOrModerateDamage, isNoDamageOrNearMissOrNotifiableDamage, requiresSubDamageLevel, DAMAGE_LEVELS } from 'utils/damage'
import { NotifyUpdateEventType } from 'validator/notify_complementary_infos'
import { NotificationProcessRequiredStepsRules, 
         EnumNotificationProcessRequiredStepsRulesStatus } from 'types/notification'
import { probalityOccurenceInfoMapper, 
         severityProbalityOccurenceInfoMapper, 
         probabilityOccurrenceRulesMapper, 
         olderRulesToShowStepsNotifcationTreatment } from 'pages/NotifyEvent/constant_text'
import { getDamage } from 'utils/damage'
import { isNotificationVersionV1 } from 'utils/notificationVersion'
import stringTable from 'string-table'
interface ObjectNotificationUpdateProps {
    getValues: UseFormGetValues<NotifyUpdateEventType>
}
/**
 * Implementação de regras de negócio para atualização de objeto de notificação no momento do update
 * @param param0 
 * @returns 
 */
export function objectNotificationUpdate({
    getValues
}: ObjectNotificationUpdateProps) {
    const object = getValues()

    if (
        object.u_image_risk.includes('yes') ||
        object?.damage_level === DAMAGE_LEVELS[0].id ||
        object?.damage_level === DAMAGE_LEVELS[1].id
    ) {
        object.u_incident_classification = object.u_incident_classification
        object.u_observation_incident_classification =
        object.u_observation_incident_classification
    } else {
        object.u_incident_classification = ''
        object.u_observation_incident_classification = ''
    }
    
    if (object.involves_patient) {
        object.patient_initials = object.patient_initials
        object.patient_date_of_birth = object.patient_date_of_birth
        object.document_type = object.document_type
        object.document_number = object.document_number
    } else {
        object.patient_initials = ''
        object.patient_date_of_birth = null
        object.document_type = ''
        object.document_number = ''
        object.patient_gender = ''
        object.u_number_patients_involved = ''
        object.u_notification_multiplier = '1'
    }
    
    if (
        !object.involves_patient ||
        (object.involves_patient &&
            object.u_number_patients_involved === 'dois_ou_mais_pacientes')
    ) {
        object.patient_date_of_birth = null
        object.document_number = ''
        object.document_type = ''
        object.patient_initials = ''
    }

    if(!object.u_attachments_url){
        object.u_attachments_string = ''
        object.u_attachments_size = ''
    }

    if(object.u_never_events_or_sentinel_event?.includes('no')){
        object.u_sentinel_event_jci = ''
        object.u_never_events_reference = ''
    }

    if (!requiresSubDamageLevel(object.damage_level)) {
        object.u_sub_damage_level = ''
    }

    const notificationVersion = (object as { u_version?: string }).u_version
    if (notificationVersion?.toLowerCase() === 'v1') {
        object.u_probability_occurence = ''
    }

    return object as ICreateNotification
}
/**
 * Formula de cálculo para obter a pontuaação final para caculo de probabilidade
 * @param notification 
 * @returns number
 */
export function getFinalScore(notification:any){
    let uProbabilityOccurence = notification?.u_probability_occurence?.value
    let damageLevelSysId = notification?.damage_level?.value
    let score = getDamage(damageLevelSysId).score
    return parseInt(uProbabilityOccurence) * score
}

/**
 * Calcula a probabilidade de ocorrência de um objeto de notificação
 * @param notification 
 * @returns 
 */
export function calculateObjectProbabilityOcurrence(notification:any){
    
    const isV1Notification = isNotificationVersionV1(notification)

    // Verificar se damage_level existe (pode não existir em notificações criadas pelo paciente)
    if(!notification?.damage_level?.value){
        return
    }
    
    let damageLevelSysId = notification?.damage_level?.value
    let damageData = getDamage(damageLevelSysId)
    
    // Verificar se getDamage retornou um valor válido
    // Nota: score pode ser 0 para óbito, então verificamos se a propriedade existe (não é undefined)
    if(!damageData || damageData.score === undefined || damageData.score === null){
        return
    }
    
    // Verificar se é óbito ou grave - para esses casos, as regras são calculadas independentemente de u_probability_occurence
    // const isObitoOrGrave = [damage_level[0].id, damage_level[1].id].includes(damageLevelSysId)
    const isObitoOrGrave = isSevereOrDeathDamage(damageLevelSysId)
    
    const hasImageRisk = notification?.u_image_risk?.value === 'u_image_risk_yes'
    
    // Se não tiver u_probability_occurence e não for óbito/grave ou risco de imagem, retornar
    if(!notification?.u_probability_occurence && !isObitoOrGrave && !hasImageRisk && !isV1Notification){
        return
    }
    
    let uProbabilityOccurence = isV1Notification
        ? '0'
        : notification?.u_probability_occurence?.value || '0'
    let score = damageData.score
    let finalScore = parseInt(uProbabilityOccurence) * score

    let notificationProcessRequiredStepsRules:NotificationProcessRequiredStepsRules = getCurrentNotificationProcessRequiredStepsRules(finalScore, notification)

    const isOlder =
        isV1Notification ||
        (notification?.u_state.value != `Nova` &&
            notification?.u_probability_occurence?.value === '')
    
    let summaryProbabiltyValues = `
        Versão do objeto de notificação: ${notification?.u_version?.display_value}\n
        Qual probabilidade? ${notification?.u_probability_occurence?.value ? `${notification?.u_probability_occurence?.value} (${probalityOccurenceInfoMapper.find(f => f.probality === parseInt(notification?.u_probability_occurence?.value))?.label})` : '(u_probability_occurence) não preenchida'}\n
        Grau do Dano da Notificação: ${damageData?.backLabel || 'Não informado'}\n
        Pontuação do Dano: ${score}\n
        Pontuação Final: ${isNaN(finalScore) ? 'Não é possível calcular' : finalScore}\n 
        Status notificação : ${notification?.u_state?.display_value || '-'}

        Então:
        ${isOlder ? 
            `
            Ishikawa, Protocolo Londres, Matrix Responsabilização e Plano de Ação estão funcionando conforme as regras antigas`
        :   `
            Ishikawa - ${notificationProcessRequiredStepsRules.ishikawaMandatory}
            Protocolo Londres - ${notificationProcessRequiredStepsRules.londonProtocolMandatory}
            Matrix Responsabilide - ${notificationProcessRequiredStepsRules.accountabilityMatrixMandatory}
            Plano de Ação - ${notificationProcessRequiredStepsRules.actionPlanMandatory}`}`

    let currentStepTreatments = []

    if(isOlder || [EnumNotificationProcessRequiredStepsRulesStatus.Optional, 
        EnumNotificationProcessRequiredStepsRulesStatus.Required].includes(notificationProcessRequiredStepsRules.ishikawaMandatory)){
            currentStepTreatments.push(2)
    }

    if(isOlder || [EnumNotificationProcessRequiredStepsRulesStatus.Optional, 
        EnumNotificationProcessRequiredStepsRulesStatus.Required].includes(notificationProcessRequiredStepsRules.londonProtocolMandatory)){
            currentStepTreatments.push(3)
    }

    if(isOlder || [EnumNotificationProcessRequiredStepsRulesStatus.Optional, 
        EnumNotificationProcessRequiredStepsRulesStatus.Required].includes(notificationProcessRequiredStepsRules.accountabilityMatrixMandatory)){
            currentStepTreatments.push(4)
    }

    if(isOlder || [EnumNotificationProcessRequiredStepsRulesStatus.Optional, 
        EnumNotificationProcessRequiredStepsRulesStatus.Required].includes(notificationProcessRequiredStepsRules.actionPlanMandatory)){
            currentStepTreatments.push(5)
    }
    
    currentStepTreatments.push(6)

    notificationProcessRequiredStepsRules.currentStepTreatments = currentStepTreatments

    if(isOlder){
        console.log('%c Usuários com permissão de escrita - Administrador, Qualidade da Unidade e Gestor do Setor', 'font-size: 10px; color:#A4B356; font-weight: bold')
        const summaryOlderRulesToShowStepsNotifcationTreatment = stringTable.create(olderRulesToShowStepsNotifcationTreatment)
        console.log(summaryOlderRulesToShowStepsNotifcationTreatment)
    }else{
        const summaryProbabiltyItens = stringTable.create(probalityOccurenceInfoMapper)
        const summarySeverityProbality = stringTable.create(severityProbalityOccurenceInfoMapper)
        const arrayProbabilityOccurrenceRulesMapper = [...probabilityOccurrenceRulesMapper]
        const summaryProbabilityOccurrenceRulesMapper = stringTable.create(arrayProbabilityOccurrenceRulesMapper)
        console.log('%c Matrix de probabilidade', 'font-size: 10px; color:#F8B3DD; font-weight: bold')
        console.log(summaryProbabiltyItens)
        //console.log(summarySeverityProbality)
        console.log('%c Tabela com regras de visualização das etapas', 'font-size: 10px; color:#FFDD77; font-weight: bold')
        console.log(summaryProbabilityOccurrenceRulesMapper)  
    }

    console.log('%c Resumo cálculo das etapas obrigatórios', 'font-size: 10px; color:#F0B323; font-weight: bold')
    console.log(summaryProbabiltyValues)
    return notificationProcessRequiredStepsRules
}


/**
 * Lista de valores e com label para <InputSelect populando uma lista de escolhas o campo `u_probability_occurence`
 * @returns Array<object>
 */
export function occurrencesList() {
    let occurrences = []
    occurrences.push({ value: "1", label: "Nunca" })
    occurrences.push({ value: "2", label: "Remoto" })
    occurrences.push({ value: "3", label: "Incomum" })
    occurrences.push({ value: "4", label: "Ocasional" })
    occurrences.push({ value: "5", label: "Frequente" })
    return occurrences    
}
/**
 * Função helper usada para defini o status do objeto de obrigagtoriedade pela matrix de responsabilidade
 * @returns Array<object>
 */
export const getCurrentNotificationProcessRequiredStepsRules = (score: number, notification: any):NotificationProcessRequiredStepsRules => {
    console.log("Checking Notification Process Required Steps Rules")
    const imageRisk = notification.u_image_risk?.value;
    const damageLevel = notification.damage_level?.value;
    const subDamageValue = notification?.u_sub_damage_level?.value
    
    let enumStatus = EnumNotificationProcessRequiredStepsRulesStatus
    let objectRules:NotificationProcessRequiredStepsRules = {  
        currentStepTreatments:[],
        ishikawaMandatory: enumStatus.Optional,
        londonProtocolMandatory: enumStatus.Optional,
        accountabilityMatrixMandatory: enumStatus.Optional,
        actionPlanMandatory: enumStatus.Optional 
    }

    if(isSevereOrDeathDamage(damageLevel) && subDamageValue === 'u_reacao_adversa'){
        objectRules.ishikawaMandatory = enumStatus.Optional
        objectRules.londonProtocolMandatory = enumStatus.Optional
        objectRules.accountabilityMatrixMandatory = enumStatus.Optional
        objectRules.actionPlanMandatory = enumStatus.Optional
    } else {
        if(imageRisk === `u_image_risk_yes` && !isSevereOrDeathDamage(damageLevel)){
            objectRules.londonProtocolMandatory = enumStatus.Required
            objectRules.accountabilityMatrixMandatory = enumStatus.Required
            objectRules.actionPlanMandatory = enumStatus.Required
        } else {
            if(isNoDamageOrNearMissOrNotifiableDamage(damageLevel)){
                if(score > 4){
                    objectRules.ishikawaMandatory = enumStatus.Required
                }
            } else if(isMildOrModerateDamage(damageLevel)){
                if(score >= 5 && score <= 10){
                    objectRules.ishikawaMandatory = enumStatus.Required
                } else if(score > 10){
                    objectRules.ishikawaMandatory = enumStatus.Required
                    objectRules.actionPlanMandatory = enumStatus.Required
                } 
            } else if(isSevereOrDeathDamage(damageLevel)) {
                objectRules.londonProtocolMandatory = enumStatus.Required
                objectRules.accountabilityMatrixMandatory = enumStatus.Required
                objectRules.actionPlanMandatory = enumStatus.Required
            }           
        }
    }
    

    return objectRules
}